"use client";

import { RiAppleFill, RiHeartFill, RiLiveLine, RiSoundcloudFill } from "@remixicon/react";
import type { ProcessedSet, Review } from "@workspace/types";
import { getB2BDisplayParts, isProgramSet } from "@workspace/types";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import { useParams } from "next/navigation";
import { type MouseEvent, useCallback } from "react";
import { Facepile } from "@/components/shared/facepile";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ArtistPhoto } from "@/components/shared/photos/artist-photo";
import { ReadOnlyStatusDisplay } from "@/components/shared/read-only-status-display";
import { SetInfoDisplay } from "@/components/shared/set-info-display";
import { SpotifyPlayButton } from "@/components/shared/spotify-play-button";
import { useSetSocialInteractions } from "@/hooks/shared";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import {
	formatDayAndTime,
	formatStageName,
} from "@/lib/business-logic/set-rendering";
import {
	getSetButtonClassName,
	getSetButtonContent,
	getSetButtonTitle,
	getSetCardActiveClassName,
	getSetCardClassName,
	getSetCardHoverClassName,
	getSetStatus,
	renderStarRating,
} from "@/lib/utils/set-card-helpers";
import { useCurrentUser } from "@/stores";

// Helper to determine which recording icon to show based on available URLs
const getRecordingIcon = (set: ProcessedSet) => {
	const hasYoutube = !!set.youtube_url;
	const hasSoundcloud = !!set.soundcloud_url;
	const hasAppleMusic = !!set.apple_music_url;

	const sourceCount = [hasYoutube, hasSoundcloud, hasAppleMusic].filter(Boolean).length;

	if (sourceCount > 1 || hasYoutube) {
		return RiLiveLine;
	}
	if (hasSoundcloud) {
		return RiSoundcloudFill;
	}
	if (hasAppleMusic) {
		return RiAppleFill;
	}
	return null;
};

interface SetCardProps {
	set: ProcessedSet;
	review: Review | undefined;
	savedSchedule?: string[];
	festivalSlug?: string;
	variant?: "personal" | "public";
	onStartReview?: (setId: string) => void;
	onToggleSetInSchedule?: (setId: string) => void;
	readOnly?: boolean;
	onAuthRequired?: () => void;
	layout?: "horizontal" | "vertical";
	hideDayLabel?: boolean;
	className?: string;
}

/**
 * Shared SetCard component that consolidates TimeSetCard and ArtistSetCard
 * Supports both horizontal (time view) and vertical (artist view) layouts
 * Eliminates code duplication while maintaining exact same functionality
 */
export const SetCard = function SetCard({
	set,
	review,
	festivalSlug,
	variant = "public",
	onStartReview = () => {},
	onToggleSetInSchedule = () => {},
	readOnly = false,
	onAuthRequired,
	layout = "horizontal",
	hideDayLabel = false,
	className,
}: SetCardProps) {
	// Use TanStack Query hook for social interactions
	const params = useParams();
	const currentUser = useCurrentUser();
	const finalFestivalSlug = festivalSlug || (params?.slug as string);
	const { socialInteractions } = useSetSocialInteractions(
		finalFestivalSlug,
		currentUser?.id || null,
		set.id,
	);

	// Get user preferences for time formatting
	const userPreferences = useUserPreferences();

	const rating = review?.rating;
	const isLoved = review?.loved;

	// Use shared status utilities
	const { status } = getSetStatus(review);

	// Memoize event handlers
	const handleStartReview = useCallback(() => {
		onStartReview(set.id);
	}, [onStartReview, set.id]);

	const handleToggleSetInSchedule = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			onToggleSetInSchedule(set.id);
		},
		[onToggleSetInSchedule, set.id],
	);

	// Keyboard accessibility for card interaction
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				handleStartReview();
			}
		},
		[handleStartReview],
	);

	// Render the rating/loved section
	const renderRatingSection = () => (
		<div className="flex items-center">
			{rating && rating > 0 ? (
				<>
					{renderStarRating({ rating, size: "md" })}
					{/* Heart if loved */}
					{isLoved && <RiHeartFill className="ml-0.5 h-4 w-4 text-red-500" />}
				</>
			) : isLoved ? (
				<RiHeartFill className="h-4 w-4 text-red-500" />
			) : null}
		</div>
	);

	// Custom subtitle renderer for horizontal layout that includes rating and facepile below time
	const renderHorizontalSubtitle = useCallback(
		(set: ProcessedSet) => {
			return (
				<div className="flex flex-col gap-1">
					{/* Show stage/time info - with TBA for lineup-only festivals */}
					<div>
						<div className="text-muted-foreground text-xs">
							{formatStageName(set)}
						</div>
						<div className="flex items-center gap-1 text-muted-foreground text-xs">
							<span>{formatDayAndTime(set, userPreferences, !hideDayLabel)}</span>
							{(() => {
								const RecordingIcon = getRecordingIcon(set);
								return RecordingIcon ? <RecordingIcon className="h-3 w-3 text-muted-foreground" /> : null;
							})()}
						</div>
					</div>
					{/* Star/heart rating row */}
					{(rating && rating > 0) || isLoved ? (
						<div className="flex items-center">
							{rating && rating > 0 ? (
								<>
									{renderStarRating({ rating, size: "md" })}
									{/* Heart if loved */}
									{isLoved && (
										<RiHeartFill className="ml-0.5 h-4 w-4 text-red-500" />
									)}
								</>
							) : isLoved ? (
								<RiHeartFill className="h-4 w-4 text-red-500" />
							) : null}
						</div>
					) : null}
					{/* Facepile - hidden on personal profile pages */}
					{variant !== "personal" &&
						socialInteractions &&
						socialInteractions.length > 0 && (
							<Facepile
								interactions={socialInteractions}
								set={set}
								festivalSlug={finalFestivalSlug}
								size="sm"
							/>
						)}
				</div>
			);
		},
		[
			socialInteractions,
			rating,
			isLoved,
			finalFestivalSlug,
			userPreferences,
			variant,
		],
	);

	// Handle auth-required click for non-authenticated users
	const handleAuthRequiredClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			onAuthRequired?.();
		},
		[onAuthRequired],
	);

	// Render the action button
	const renderActionButton = () => {
		// True read-only mode (viewing other user's profile)
		if (readOnly && !onAuthRequired) {
			return (
				<ReadOnlyStatusDisplay set={set} review={review} layout={layout} />
			);
		}

		// Non-auth user on public page - show button that opens auth modal
		if (readOnly && onAuthRequired) {
			return (
				<button
					type="button"
					className={cn(
						"flex items-center justify-center gap-1 transition-all duration-200",
						"active:scale-[0.99] active:transition-transform active:duration-100",
						layout === "horizontal"
							? "min-w-[4rem] flex-shrink rounded-lg px-1.5 py-1 text-xs sm:min-w-[5rem] sm:px-2 sm:text-sm"
							: "w-full min-w-0 whitespace-nowrap rounded-lg border py-1.5 text-center text-sm",
						getSetButtonClassName("default"),
					)}
					onClick={handleAuthRequiredClick}
					title="Sign in to add to your list"
				>
					{getSetButtonContent(
						"default",
						set,
						layout === "vertical" ? false : undefined,
					)}
				</button>
			);
		}

		// Authenticated user - normal interactive button
		return (
			<button
				type="button"
				className={cn(
					"flex items-center justify-center gap-1 transition-all duration-200",
					"active:scale-[0.99] active:transition-transform active:duration-100",
					layout === "horizontal"
						? "min-w-[4rem] flex-shrink rounded-lg px-1.5 py-1 text-xs sm:min-w-[5rem] sm:px-2 sm:text-sm"
						: "w-full min-w-0 whitespace-nowrap rounded-lg border py-1.5 text-center text-sm",
					getSetButtonClassName(status),
				)}
				onClick={handleToggleSetInSchedule}
				title={getSetButtonTitle(status, set)}
			>
				{getSetButtonContent(
					status,
					set,
					layout === "vertical" ? false : undefined,
				)}
			</button>
		);
	};

	if (layout === "horizontal") {
		// TimeSetCard layout
		return (
			<Card
				key={set.id}
				className={cn(
					"w-full overflow-hidden rounded-xl",
					getSetCardClassName(status),
					getSetCardHoverClassName(status),
					getSetCardActiveClassName(status),
					"transition-all duration-150",
					"cursor-pointer",
					"active:scale-[0.99]",
					className,
				)}
				onClick={handleStartReview}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				role="button"
				aria-label={`View details for ${set.artist?.name || "set"}`}
			>
				<CardContent className="p-2 sm:p-3">
					<div className="flex items-start justify-between gap-2 sm:gap-3">
						<div className="min-w-0 flex-1 overflow-hidden">
							<SetInfoDisplay
								set={set}
								showLiveIndicator={true}
								titleElement="h4"
								context="card"
								renderSubtitle={renderHorizontalSubtitle}
								photoType="artist"
								photoSize="lg"
							/>
						</div>

						{/* List Button */}
						{renderActionButton()}
					</div>
				</CardContent>
			</Card>
		);
	}

	// ArtistSetCard layout (vertical)
	return (
		<Card
			key={set.id}
			className={cn(
				"relative flex h-full flex-col overflow-hidden rounded-xl",
				getSetCardClassName(status),
				getSetCardHoverClassName(status),
				getSetCardActiveClassName(status),
				"transition-all duration-150",
				"cursor-pointer",
				"active:scale-[0.99]",
				className,
			)}
			onClick={handleStartReview}
			onKeyDown={handleKeyDown}
			tabIndex={0}
			role="button"
			aria-label={`View details for ${set.artist?.name || "set"}`}
		>
			<CardContent className="flex h-full flex-col p-3">
				{/* Photo section */}
				<div className="relative mb-3 aspect-square w-full overflow-hidden rounded-lg">
					<ArtistPhoto
						name={set.artist?.name || "Artist"}
						imageUrl={set.is_surprise ? null : set.artist?.image_url}
						isSurprise={set.is_surprise}
						isProgram={isProgramSet(set)}
						size="xl"
						aspectRatio="square"
						className="absolute inset-0 h-full w-full"
						rounded="card"
					/>
					{/* Spotify play button overlay */}
					{!isProgramSet(set) && set.artist?.spotify_id && (
						<div className="absolute right-2 bottom-2">
							<SpotifyPlayButton
								spotifyId={set.artist.spotify_id}
								artistName={set.artist.name}
							/>
						</div>
					)}
				</div>

				{/* Info section - all content grouped together */}
				<div className="flex flex-1 flex-col">
					{/* Top content group */}
					<div className="flex flex-col gap-2">
						{/* Group 1: Artist name, stage, and time (no gap) */}
						<div>
							{/* Artist name with B2B support */}
							<h4 className="line-clamp-2 font-semibold leading-tight">
								{(() => {
									// Use the same logic as SetInfoDisplay for consistency
									if (set.is_surprise) {
										return set.placeholder_name || "Surprise Guest";
									}
									const displayParts = getB2BDisplayParts(set);

									// For simple cases (single artist or 2-artist B2B)
									if (!displayParts.parts || displayParts.parts.length === 0) {
										return (
											<>
												{displayParts.mainText}
												{displayParts.connector && (
													<span className="ml-1 font-normal text-muted-foreground/50">
														{displayParts.connector}
													</span>
												)}
												{displayParts.secondaryText && (
													<span className="ml-1">
														{displayParts.secondaryText}
													</span>
												)}
											</>
										);
									}

									// For complex cases (3+ artists)
									return displayParts.parts.map((part, index) => (
										<span key={index}>
											{part.type === "artist" || part.type === "presents" ? (
												part.text
											) : (
												<span className="font-normal text-muted-foreground/50">
													{part.text}
												</span>
											)}
											{index < (displayParts.parts?.length ?? 0) - 1 && " "}
										</span>
									));
								})()}
							</h4>

							{/* Stage and Time info - show TBA for lineup-only festivals */}
							<div>
								<div className="text-muted-foreground text-xs">
									{formatStageName(set)}
								</div>
								<div className="flex items-center gap-1 text-muted-foreground text-xs">
									<span>{formatDayAndTime(set, userPreferences, !hideDayLabel)}</span>
									{(() => {
										const RecordingIcon = getRecordingIcon(set);
										return RecordingIcon ? <RecordingIcon className="h-3 w-3 text-muted-foreground" /> : null;
									})()}
								</div>
							</div>
						</div>

						{/* Group 2: Genres, rating, and facepile (with gap-2) */}
						<div className="flex flex-col gap-2">
							{/* Genres */}
							{set.artist && (
								<GenreBadgeListFromData
									spotifyGenres={set.artist.spotify_genres || null}
									manualGenres={set.artist.manual_genres || null}
									maxDisplay={3}
								/>
							)}

							{/* Rating section */}
							{((rating && rating > 0) || isLoved) && (
								<div className="flex items-center">{renderRatingSection()}</div>
							)}

							{/* Social facepile - hidden on personal profile pages */}
							{variant !== "personal" &&
								layout === "vertical" &&
								socialInteractions &&
								socialInteractions.length > 0 && (
									<Facepile
										interactions={socialInteractions}
										set={set}
										festivalSlug={finalFestivalSlug}
										size="sm"
									/>
								)}
						</div>
					</div>

					{/* Action button row - pushed to bottom */}
					<div className="mt-auto pt-3">
						{renderActionButton()}
					</div>
				</div>
			</CardContent>
		</Card>
	);
};

SetCard.displayName = "SetCard";
